import React, { ReactElement } from "react";

import { isNil } from "ramda";

import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { IClusterData } from "types/api/serversApiInterface";

const ClusterNameStat = ({
 name,
 cluster
}: {
 name: string;
 cluster: IClusterData;
}): ReactElement => {
 const renderStatusColor = (
  status: string | undefined
 ): "error" | "warning" | "success" | "default" => {
  if (isNil(status)) {
   return "default";
  } else if (status === "Connected" || status === "Ready" || status === "Active") {
   return "success";
  } else {
   return "error";
  }
 };

 const formatPercent = (): number => {
  if (
   cluster.cluster_volume &&
   cluster.cluster_volume.disk_space_free &&
   cluster.cluster_volume.disk_space_free
  ) {
   return (
    (Number(
     cluster.cluster_volume.disk_space_free.substring(
      0,
      cluster.cluster_volume.disk_space_free.length - 2
     )
    ) /
     Number(
      cluster.cluster_volume.total_disk_space.substring(
       0,
       cluster.cluster_volume.total_disk_space.length - 2
      )
     )) *
    100
   );
  } else {
   return 0;
  }
 };

 const performanceColor =
  formatPercent() < 50 ? "#41C58C" : formatPercent() < 75 ? "#FF9800" : "#F44336";

 return (
  <Stack textAlign="left" spacing={1} minWidth={300}>
   <Typography textTransform="uppercase" fontWeight="bold">
    {name}
   </Typography>
   <Stack direction="row" alignItems="center">
    <Typography variant="extraSmall" minWidth={80}>
     Cluster
    </Typography>
    <Chip
     size="small"
     label={
      <Typography variant="caption">{cluster.cluster_availability || "Not available"}</Typography>
     }
     color={renderStatusColor(cluster.cluster_availability)}
     sx={{ height: 14 }}
    />
   </Stack>
   <Stack direction="row" alignItems="center">
    <Typography variant="extraSmall" minWidth={80}>
     Status
    </Typography>
    <Chip
     size="small"
     label={<Typography variant="caption">{cluster.cluster_status || "Not available"}</Typography>}
     color={renderStatusColor(cluster.cluster_status)}
     sx={{ height: 14 }}
    />
   </Stack>
   <Stack direction="row" alignItems="center">
    <Typography variant="extraSmall" minWidth={80}>
     System
    </Typography>
    <Chip
     size="small"
     label={
      <Typography variant="caption">{cluster.cluster_health?.status || "Not available"}</Typography>
     }
     color={renderStatusColor(cluster.cluster_health?.status)}
     sx={{ height: 14 }}
    />
   </Stack>
   <Stack direction="row" alignItems="center">
    <Typography variant="extraSmall" minWidth={80}>
     Volume FS
    </Typography>
    {cluster.cluster_volume?.disk_space_free && cluster.cluster_volume?.total_disk_space ? (
     <Stack>
      <Typography variant="extraSmall">{`${cluster.cluster_volume?.disk_space_free}/${cluster.cluster_volume?.total_disk_space}`}</Typography>
      <LinearProgress
       sx={{
        width: "100%",
        backgroundColor: "#F1F2F7",
        "& .MuiLinearProgress-bar": {
         backgroundColor: performanceColor
        },
        height: 10,
        borderRadius: 0.8
       }}
       variant="determinate"
       value={formatPercent()}
      />
     </Stack>
    ) : (
     <Stack px={1}>
      <Typography variant="extraSmall">Not available</Typography>
      <LinearProgress
       sx={{
        width: "100%",
        backgroundColor: "#F1F2F7",
        "& .MuiLinearProgress-bar": {
         backgroundColor: performanceColor
        },
        height: 10,
        borderRadius: 0.8
       }}
       variant="determinate"
       value={0}
      />
     </Stack>
    )}
   </Stack>
  </Stack>
 );
};

export default ClusterNameStat;
